/**
 * หน้าจอคิวข้อขัดแย้ง Offline Sync — ปลายทางฝั่งคนของวง offline-first
 *
 * คำสั่งที่เครื่องสแกนส่งขึ้นมาแล้วเซิร์ฟเวอร์ปฏิเสธด้วยกฎธุรกิจ ไม่ได้หายไปและไม่ได้
 * ถูก force ให้ลง มันถูกบันทึกเป็น `offline_sync_conflict` แล้วมารอที่นี่ให้ **คน**
 * ตัดสิน (กฎ Sync ข้อ 4-5, D-077)
 *
 * **สิ่งที่หน้าจอนี้ทำไม่ได้ และต้องพูดออกมาให้ดัง: มันไม่ทำให้คำสั่งลงจริง**
 * ปุ่มทั้งสองปุ่มยิงไปที่ `POST /v1/offline-sync/conflicts/{id}/resolve` ซึ่งเขียน
 * เพียง `resolution`/`resolved_by_employee_id`/`resolved_at` กับ `audit_log` —
 * ไม่มีสต๊อกเคลื่อน ไม่มีเอกสารบัญชี ไม่มีการเรียก scan/staging service ใด ๆ
 * (business rule 5) ถ้าเหตุที่ทำให้ถูกปฏิเสธถูกแก้แล้วและงานต้องลงจริง **ต้องสแกน
 * ใหม่ที่หน้างาน** เป็นคำสั่งใหม่ที่มี `client_event_id` ใหม่ ไม่ใช่ปลดล็อกใบเดิม
 *
 * การ gate สิทธิ์เป็นแบบเดียวกับ `SystemRolesScreen`: `SHOP_FLOOR` + VIEW เป็นสิทธิ์
 * ของ **การเปิดหน้าจอนี้** (ด่านอยู่ที่ route) ส่วน `SHOP_FLOOR` + APPROVE เป็นสิทธิ์
 * ของ **การบันทึกการตัดสิน** ซึ่งถูกบังคับข้างในหน้าจอตรงที่ของมัน — ผู้ที่มีแต่ VIEW
 * เห็นคิวครบทุกแถวแต่ไม่มีปุ่ม
 *
 * สถานะของตารางมาจาก `GET /v1/offline-sync/conflicts` เสมอ และเปลี่ยนได้ทางเดียวคือ
 * โหลดใหม่ **หลัง** ที่ `POST` ตอบกลับ ไม่มี optimistic update ในไฟล์นี้เลย: แถวที่
 * หายไปจากจอก่อนเซิร์ฟเวอร์ยืนยัน คือคำสั่งที่ถูกปฏิเสธซึ่งไม่มีใครดูอีกเลย
 *
 * ⚠️ ทั้งหมดนี้เป็น UX layer ไม่ใช่ด่านความปลอดภัย เซิร์ฟเวอร์เป็นผู้ตัดสินทุก
 * request เสมอ (`references/authorization-rbac.md` ข้อ 5)
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';

import { OfflineConflictFailureNotice } from './OfflineConflictFailureNotice';
import {
  RESOLVED_PARAM,
  commandTypeLabel,
  reasonDescription,
  reasonTone,
  resolutionLabel,
  resolutionTone,
  showsResolved,
} from './offlineConflictsView';
import { InfoHint } from '../components/InfoHint';
import { StatusBadge } from '../StatusBadge';
import { can } from '../../lib/permissions/client';
import type { PermissionsState } from '../../lib/permissions/types';
import {
  fetchOfflineSyncConflicts,
  resolveOfflineSyncConflict,
  type OfflineConflictFailure,
} from '../../lib/offlineConflicts/client';
import {
  CONFLICT_REJECTED,
  CONFLICT_RESOLVED,
  SHOP_FLOOR_MODULE,
  type ConflictOutcome,
  type OfflineSyncConflict,
} from '../../lib/offlineConflicts/types';

export interface OfflineConflictsScreenProps {
  readonly permissions: PermissionsState;
  readonly apiBaseUrl: string;
  /** inject ได้เพื่อการทดสอบ — production ใช้ `apiFetch` ผ่าน client */
  readonly fetchImpl?: typeof fetch;
}

type ConflictsState =
  | { readonly status: 'loading' }
  | {
      readonly status: 'ready';
      readonly items: readonly OfflineSyncConflict[];
      readonly nextCursor: string | null;
      readonly loadingMore: boolean;
      /** ความล้มเหลวของ "โหลดเพิ่ม" ไม่ควรลบแถวที่โหลดมาแล้วทิ้ง */
      readonly loadMoreFailure: OfflineConflictFailure | null;
    }
  | OfflineConflictFailure;

/** คำขอที่กำลังเดินทางอยู่ — ใช้ปิดปุ่มทั้งหน้าจอระหว่างนั้น */
interface PendingDecision {
  readonly conflictId: string;
  readonly outcome: ConflictOutcome;
}

interface DecisionFailure {
  readonly conflictId: string;
  readonly failure: OfflineConflictFailure;
}

export function OfflineConflictsScreen({
  permissions,
  apiBaseUrl,
  fetchImpl,
}: OfflineConflictsScreenProps) {
  const canApprove = can(permissions, SHOP_FLOOR_MODULE, 'APPROVE');

  const [searchParams, setSearchParams] = useSearchParams();
  const resolvedView = showsResolved(searchParams);

  const [state, setState] = useState<ConflictsState>({ status: 'loading' });
  const [reloadToken, setReloadToken] = useState(0);
  const [pending, setPending] = useState<PendingDecision | null>(null);
  const [decisionFailure, setDecisionFailure] = useState<DecisionFailure | null>(null);
  const [decisionResult, setDecisionResult] = useState<OfflineSyncConflict | null>(null);

  useEffect(() => {
    const controller = new AbortController();
    let active = true;

    // ระหว่างโหลดซ้ำหลังบันทึกการตัดสิน **คิวเดิมยังอยู่** ไม่กระพริบเป็นหน้าว่าง:
    // ผู้ใช้เพิ่งกดปุ่มไป สิ่งที่เขาต้องเห็นคือผลของมัน ไม่ใช่หน้าจอที่หายไปทั้งจอ
    setState((previous) => (previous.status === 'ready' ? previous : { status: 'loading' }));

    void fetchOfflineSyncConflicts(
      { resolved: resolvedView },
      { apiBaseUrl, fetchImpl, signal: controller.signal },
    ).then((result) => {
      if (!active || result.status === 'cancelled') {
        return;
      }
      setState(
        result.status === 'ready'
          ? {
              status: 'ready',
              items: result.page.items,
              nextCursor: result.page.next_cursor,
              loadingMore: false,
              loadMoreFailure: null,
            }
          : result,
      );
    });

    return () => {
      active = false;
      controller.abort();
    };
  }, [apiBaseUrl, fetchImpl, resolvedView, reloadToken]);

  const reload = () => {
    setReloadToken((token) => token + 1);
  };

  const switchView = (next: boolean) => {
    if (next === resolvedView) {
      return;
    }
    // เปลี่ยนมุมมอง = คำถามใหม่ ผลของการกดปุ่มครั้งก่อนจึงไม่ควรค้างข้ามไป
    setDecisionResult(null);
    setDecisionFailure(null);
    setSearchParams(next ? { [RESOLVED_PARAM]: 'true' } : {});
  };

  const loadMore = async () => {
    if (state.status !== 'ready' || state.loadingMore || state.nextCursor === null) {
      return;
    }
    const loaded = state;
    const cursor = loaded.nextCursor;
    if (cursor === null) {
      return;
    }
    setState({ ...loaded, loadingMore: true, loadMoreFailure: null });

    const result = await fetchOfflineSyncConflicts(
      { resolved: resolvedView, cursor },
      { apiBaseUrl, fetchImpl },
    );
    if (result.status === 'cancelled') {
      return;
    }
    setState(
      result.status === 'ready'
        ? {
            status: 'ready',
            items: [...loaded.items, ...result.page.items],
            nextCursor: result.page.next_cursor,
            loadingMore: false,
            loadMoreFailure: null,
          }
        : { ...loaded, loadingMore: false, loadMoreFailure: result },
    );
  };

  /**
   * บันทึกการตัดสินหนึ่งครั้ง — เส้นทางเขียนเส้นเดียวของหน้าจอนี้
   *
   * ทั้งสองปุ่มเดินผ่านฟังก์ชันนี้ ต่างกันที่ `outcome` เท่านั้น และไม่มีกิ่งไหนที่
   * ยิงไปหา endpoint อื่น — หน้าจอนี้ไม่มีเส้นทางที่ทำให้คำสั่งลงจริง (D-077)
   */
  const submitDecision = async (conflictId: string, outcome: ConflictOutcome) => {
    if (!canApprove || pending !== null) {
      return;
    }

    setPending({ conflictId, outcome });
    setDecisionFailure(null);
    setDecisionResult(null);

    const result = await resolveOfflineSyncConflict(conflictId, outcome, {
      apiBaseUrl,
      fetchImpl,
    });
    setPending(null);
    if (result.status === 'cancelled') {
      return;
    }
    if (result.status !== 'ready') {
      setDecisionFailure({ conflictId, failure: result });
      // `409` แปลว่าคนอื่นตัดสินไปแล้ว และ `404` แปลว่ามันไม่อยู่ในมุมมองนี้อีก —
      // ทั้งสองกรณีสิ่งที่อยู่บนจอเก่ากว่าความจริง จึงต้องโหลดใหม่ ไม่ใช่ปล่อยให้
      // ผู้ใช้กดปุ่มเดิมซ้ำไปเรื่อย ๆ กับแถวที่ไม่ใช่ของเขาแล้ว
      if (result.status === 'conflict' || result.status === 'not-found') {
        reload();
      }
      return;
    }

    // อ่านสถานะใหม่จากคำตอบของเซิร์ฟเวอร์ ไม่ใช่จากปุ่มที่เพิ่งกด แล้วโหลดคิวใหม่
    // ทั้งชุดเพื่อให้ตรงกับฐานข้อมูล รวมถึงแถวที่คนอื่นเพิ่งตัดสินไปพร้อมกัน
    setDecisionResult(result.conflict);
    reload();
  };

  const busy = pending !== null;
  const items = state.status === 'ready' ? state.items : [];

  return (
    <div className="cap-page" data-testid="offline-conflicts">
      <div className="cap-page__head">
        <div>
          <h1 className="cap-page__title">
            ข้อขัดแย้งจากการซิงก์เครื่องสแกน{' '}
            <InfoHint
              label="เกี่ยวกับหน้าข้อขัดแย้งจากการซิงก์"
              text="คำสั่งหน้างานที่เครื่องสแกนส่งขึ้นมาแล้วเซิร์ฟเวอร์ปฏิเสธตามกฎธุรกิจ จะไม่ถูก บันทึกเข้าระบบเองและไม่ถูกบังคับให้ลง แต่ถูกพักไว้ที่นี่เพื่อให้คนตัดสิน"
            />
          </h1>
        </div>
      </div>

      {/**
       * ประโยคที่แพงที่สุดของหน้าจอนี้ อยู่เหนือปุ่มที่ผู้ใช้กำลังจะกด ไม่ใช่เชิงอรรถ
       * ท้ายหน้า — ผู้ที่เข้าใจผิดว่าปุ่มนี้ "ส่งคำสั่งเข้าไปใหม่" จะกดมันแล้วเดินจากไป
       * โดยคิดว่างานหน้างานลงระบบแล้ว ทั้งที่สต๊อกและบัญชีไม่ได้ขยับเลย
       */}
      <div className="cap-notice" data-testid="offline-conflicts-decision-only" role="status">
        <StatusBadge tone="info">อ่านก่อนกด</StatusBadge>
        <h2 className="cap-notice__title">
          การปิดรายการที่นี่คือการบันทึกการตัดสินของคน ไม่ใช่การส่งคำสั่งเข้าระบบใหม่
        </h2>
        <p className="cap-notice__text">
          ทั้งสองปุ่มบันทึกเพียงว่า <strong>ใครตัดสินว่าอย่างไรและเมื่อไร</strong>{' '}
          ไม่มีการทำคำสั่งเดิมซ้ำ ไม่มีสต๊อกเคลื่อน และไม่มีเอกสารบัญชีเกิดขึ้นจากหน้าจอนี้
          (กฎธุรกิจข้อ 5 — D-077)
        </p>
        <p className="cap-notice__note">
          ถ้าเหตุที่ทำให้ถูกปฏิเสธถูกแก้แล้วและงานต้องลงระบบจริง ให้
          <strong> สแกนใหม่ที่หน้างาน</strong> เป็นคำสั่งใหม่ ไม่ใช่ปิดรายการเดิมจากที่นี่
        </p>
      </div>

      <div className="cap-filters" role="group" aria-label="มุมมองของคิว">
        <button
          type="button"
          className={`cap-button ${resolvedView ? 'cap-button--ghost' : 'cap-button--primary'}`}
          data-testid="offline-conflicts-view-open"
          aria-pressed={!resolvedView}
          onClick={() => {
            switchView(false);
          }}
        >
          ที่ยังไม่ตัดสิน
        </button>
        <button
          type="button"
          className={`cap-button ${resolvedView ? 'cap-button--primary' : 'cap-button--ghost'}`}
          data-testid="offline-conflicts-view-resolved"
          aria-pressed={resolvedView}
          onClick={() => {
            switchView(true);
          }}
        >
          ตัดสินแล้ว (อ่านอย่างเดียว)
        </button>
      </div>

      {!canApprove ? (
        <div className="cap-notice" data-testid="offline-conflicts-read-only" role="status">
          <StatusBadge tone="warning">ดูได้อย่างเดียว</StatusBadge>
          <h2 className="cap-notice__title">สิทธิ์ชุดนี้ยังบันทึกการตัดสินไม่ได้</h2>
          <p className="cap-notice__text">
            สิทธิ์ที่เซิร์ฟเวอร์ส่งมาไม่มี flag <code className="cap-table__code">can_approve</code>{' '}
            ของโมดูล <code className="cap-table__code">{SHOP_FLOOR_MODULE}</code>{' '}
            ปุ่มปิดรายการจึงไม่แสดง แต่คิวทั้งหมดยังอ่านได้ครบ
          </p>
          <p className="cap-notice__note">
            การซ่อนปุ่มเป็นเพียง UX เพื่อไม่ให้กดสิ่งที่จะถูกปฏิเสธ — ผู้ตัดสินจริงคือ backend
            ทุกคำขอเสมอ
          </p>
        </div>
      ) : null}

      {decisionResult === null ? null : (
        <div className="cap-notice" data-testid="offline-conflict-decision-result" role="status">
          <StatusBadge tone={resolutionTone(decisionResult.resolution)}>บันทึกแล้ว</StatusBadge>
          <h2 className="cap-notice__title">
            บันทึกการตัดสิน “{resolutionLabel(decisionResult.resolution) ?? '—'}” ของรายการ{' '}
            {decisionResult.conflict_id} แล้ว
          </h2>
          <p className="cap-notice__text">
            ผู้ตัดสิน: {decisionResult.resolved_by_employee_id ?? '—'} เมื่อ{' '}
            {decisionResult.resolved_at ?? '—'} — ค่าเหล่านี้มาจากคำตอบของเซิร์ฟเวอร์
            ไม่ใช่จากปุ่มที่เพิ่งกด
          </p>
          <p className="cap-notice__note">
            คำสั่งเดิมยังไม่ถูกบันทึกเข้าระบบและจะไม่ถูกบันทึกจากหน้าจอนี้
          </p>
        </div>
      )}

      {decisionFailure === null ? null : (
        <OfflineConflictFailureNotice
          failure={decisionFailure.failure}
          context="decision"
          testId="offline-conflict-decision-failure"
        />
      )}

      {state.status === 'loading' ? (
        <div className="cap-notice" data-testid="offline-conflicts-loading" role="status">
          <p className="cap-notice__text">กำลังโหลดคิวข้อขัดแย้ง…</p>
        </div>
      ) : null}

      {state.status !== 'loading' && state.status !== 'ready' ? (
        <OfflineConflictFailureNotice
          failure={state}
          context="list"
          testId="offline-conflicts-failure"
          onRetry={reload}
        />
      ) : null}

      {state.status === 'ready' && items.length === 0 ? (
        <div className="cap-notice" data-testid="offline-conflicts-empty" role="status">
          <StatusBadge tone="success">ไม่มีรายการค้าง</StatusBadge>
          <h2 className="cap-notice__title">
            {resolvedView
              ? 'ยังไม่มีรายการที่ถูกตัดสิน'
              : 'ไม่มีคำสั่งหน้างานที่รอการตัดสิน'}
          </h2>
          <p className="cap-notice__text">
            เซิร์ฟเวอร์ตอบสำเร็จแต่รายการว่างเปล่า — เป็นคำตอบปกติ ไม่ใช่การโหลดที่ล้มเหลว
          </p>
        </div>
      ) : null}

      {state.status === 'ready' && items.length > 0 ? (
        <section className="cap-card cap-card--table" aria-label="คิวข้อขัดแย้งจากการซิงก์">
          <div className="cap-card__head">
            <h2 className="cap-card__title">
              {resolvedView ? 'รายการที่ตัดสินแล้ว' : 'รายการที่รอการตัดสิน'}{' '}
              <InfoHint
                label="เกี่ยวกับลำดับของรายการ"
                text="เรียงจากรายการที่รอนานที่สุด — ลำดับมาจากเซิร์ฟเวอร์ หน้าจอไม่จัดเรียงใหม่"
              />
            </h2>
          </div>
          <div className="cap-table-wrap">
            <table className="cap-table" data-testid="offline-conflicts-table">
              <caption className="cap-visually-hidden">
                คำสั่งจากเครื่องสแกนที่เซิร์ฟเวอร์ปฏิเสธ พร้อมเหตุผลและปุ่มบันทึกการตัดสิน —
                การปิดรายการไม่ได้ทำให้คำสั่งเดิมถูกบันทึกเข้าระบบ
              </caption>
              <thead>
                <tr>
                  <th scope="col">เวลาที่บันทึก</th>
                  <th scope="col">ชนิดคำสั่ง</th>
                  <th scope="col">รหัสคำสั่งของเครื่อง</th>
                  <th scope="col">เหตุผลที่ถูกปฏิเสธ</th>
                  <th scope="col">{resolvedView ? 'ผลการตัดสิน' : 'บันทึกการตัดสิน'}</th>
                </tr>
              </thead>
              <tbody>
                {items.map((conflict) => (
                  <tr
                    key={conflict.conflict_id}
                    data-testid={`offline-conflict-row-${conflict.conflict_id}`}
                  >
                    <th scope="row" className="cap-table__row-head">
                      {conflict.created_at}
                      <br />
                      <code className="cap-table__code">{conflict.conflict_id}</code>
                    </th>
                    <td data-testid={`offline-conflict-command-${conflict.conflict_id}`}>
                      {commandTypeLabel(conflict.command_type)}
                    </td>
                    <td>
                      <code
                        className="cap-table__code"
                        data-testid={`offline-conflict-event-${conflict.conflict_id}`}
                      >
                        {conflict.client_event_id}
                      </code>
                    </td>
                    <td data-testid={`offline-conflict-reason-${conflict.conflict_id}`}>
                      <StatusBadge tone={reasonTone(conflict.reason)}>
                        {conflict.reason}
                      </StatusBadge>
                      {reasonDescription(conflict.reason) === null ? null : (
                        <span className="cap-field__hint">
                          {reasonDescription(conflict.reason)}
                        </span>
                      )}
                    </td>
                    <td>
                      {conflict.resolved_at !== null ? (
                        <span data-testid={`offline-conflict-resolution-${conflict.conflict_id}`}>
                          <StatusBadge tone={resolutionTone(conflict.resolution)}>
                            {resolutionLabel(conflict.resolution) ?? '—'}
                          </StatusBadge>
                          <span className="cap-field__hint">
                            {conflict.resolved_by_employee_id ?? '—'} · {conflict.resolved_at}
                          </span>
                        </span>
                      ) : canApprove ? (
                        <>
                          <button
                            type="button"
                            className="cap-button cap-button--primary"
                            data-testid={`offline-conflict-resolve-${conflict.conflict_id}`}
                            disabled={busy}
                            onClick={() => {
                              void submitDecision(conflict.conflict_id, CONFLICT_RESOLVED);
                            }}
                          >
                            {busy &&
                            pending?.conflictId === conflict.conflict_id &&
                            pending.outcome === CONFLICT_RESOLVED
                              ? 'กำลังบันทึก…'
                              : 'จัดการแล้ว'}
                          </button>
                          <button
                            type="button"
                            className="cap-button cap-button--ghost"
                            data-testid={`offline-conflict-reject-${conflict.conflict_id}`}
                            disabled={busy}
                            onClick={() => {
                              void submitDecision(conflict.conflict_id, CONFLICT_REJECTED);
                            }}
                          >
                            {busy &&
                            pending?.conflictId === conflict.conflict_id &&
                            pending.outcome === CONFLICT_REJECTED
                              ? 'กำลังบันทึก…'
                              : 'ไม่ทำคำสั่งนี้'}
                          </button>
                        </>
                      ) : (
                        <span className="cap-field__hint">ดูได้อย่างเดียว</span>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {state.loadMoreFailure === null ? null : (
            <OfflineConflictFailureNotice
              failure={state.loadMoreFailure}
              context="list"
              testId="offline-conflicts-load-more-failure"
            />
          )}

          {state.nextCursor === null ? null : (
            <div className="cap-card__foot">
              <button
                type="button"
                className="cap-button cap-button--ghost"
                data-testid="offline-conflicts-load-more"
                disabled={state.loadingMore}
                onClick={() => {
                  void loadMore();
                }}
              >
                {state.loadingMore ? 'กำลังโหลด…' : 'โหลดรายการถัดไป'}
              </button>
            </div>
          )}
        </section>
      ) : null}
    </div>
  );
}
